import { apiUtils } from '../hooks';
import { Hotel } from '../hooks/useHotels';
import { throwApiError } from '../utils';
import { API_ENDPOINTS } from './apiEndpoints';


export type ScoreViewType = 'full' | 'fasttrack';
export interface Quarter {
  id: string;
  quarter_label: string;
  fiscal_quarter: number;
  fiscal_year: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_latest: boolean;
}
export interface EnterpriseDetails {
  id: string;
  name: string;
  financial_quarter: string;
  latest_quarter_id: string;
  available_quarters: Quarter[];
}
export interface ProgramSummary {
  enterprise: {
    id: string;
    name: string;
    financial_quarter: string;
  };
  program_score: {
    avg_alo_score_from_hotel: number;
    avg_e_score_from_hotel: number;
    avg_s_score_from_hotel: number;
    avg_g_score_from_hotel: number;
    avg_alo_score: number;
    avg_e_score: number;
    avg_s_score: number;
    avg_g_score: number;
    e: {
      biodiversity: number;
      climate: number;
      energy: number;
      waste: number;
      water: number;
    };
    s: {
      community: number;
      human_rights: number;
      responsible_sourcing: number;
      accessibility: number;
    };
    g: {
      auditability: number;
      training: number;
      strategy: number;
    };
    fundamental_score: Hotel['fundamental_score'];
    fundamental_score_percentages: Hotel['fundamental_score'];
    total_properties: string;
    total_room_nights: string;
    preferred_properties: string;
    preferred_properties_percentage: string;
    preferred_room_nights_percentage: string;
    total_preferred_hotels: string;
    total_countries: string;
    total_markets: string;
    preferred_properties_with_room_nights: string; //preferred properties in the card
    preferred_room_nights: string;
    preferred_room_nights_scored_percentage: number;
    scored_hotels: string;
    unscored_hotels: string;
    scored_hotels_percentage: number;
    preferred_scored_hotels_percentage: number;
    unscored_inprogress_hotels_percentage: number;
    fundamental_awarded_count: number;
    fundamental_total: number;
    fundamental_scored_hotels: number;
    fundamental_scored_hotels_percentage: number;
    fundamental_preferred_scored_hotels: number;
    fundamental_preferred_scored_hotels_percentage: number;
    fundamental_preferred_room_nights_scored: number;
    fundamental_preferred_room_nights_scored_percentage: number;
  };
}
interface MarketsHotelData {
  id: string;
  hotel_location_longitude: number;
  hotel_location_latitude: number;
  hotel_location_name: string;
  hotel_location_city: string;
  hotel_location_state: string;
  hotel_location_country: string;
  hotel_location_country_code: string;
  country: string;
  score_alo: number;
  score_e: number;
  score_s: number;
  score_g: number;
}
export interface MarketSummary {
  markets: Array<{
    market: string;
    state: string;
    country_code: string;
    country: string;
    avg_alo_score_from_hotel: number;
    avg_e_score_from_hotel: number;
    avg_s_score_from_hotel: number;
    avg_g_score_from_hotel: number;
    avg_alo_score: number;
    avg_e_score: number;
    avg_s_score: number;
    avg_g_score: number;
    avg_e_biodiversity: number;
    avg_e_climate: number;
    avg_e_energy: number;
    avg_e_waste: number;
    avg_e_water: number;
    avg_s_community: number;
    avg_s_human_rights: number;
    avg_s_responsible_sourcing: number;
    avg_s_accessibility: number;
    avg_g_auditability: number;
    avg_g_training: number;
    avg_g_strategy: number;
    total_hotels: number;
    total_countries: number;
    preferred_properties: number;
    scored_hotels: number;
    unscored_hotels: number;
    unscored_and_not_started_hotels: number;
    preferred_scored_hotels: number;
    preferred_unscored_hotels: number;
    preferred_room_nights: number;
    hotels: MarketsHotelData[];
  }>;
}
export interface MarketSummaryResponse {
  data: MarketSummary;
  pagination: {
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    totalCount: number;
    itemsPerPage: number;
  };
}

export interface ValueMatrixHotel {
  id: string;
  name: string;
  economyScore: string;
  hotelScore: number;
  planetScore: number;
  peopleScore: number;
  policyScore: number;
  economyScoreNormalized: number;
  hotelScoreNormalized: number;
  roomNights: number;
  roomNightsNormalized: number;
  city: string;
}

export interface ValueMatrixResponse {
  message: string;
  data: {
    enterprise: {
      id: string;
      name: string;
      financial_quarter: string | null;
    };
    market: {
      city: string;
      state: string;
      country_code: string;
    };
    hotels: ValueMatrixHotel[];
  };
  _timezone: {
    requested: string;
    applied: boolean;
  };
}
class EnterpriseAdminService {
  async getEnterpriseDetails(enterpriseId?: string) {
    try {
      const response = await apiUtils.get<{ data: EnterpriseDetails }>(
        API_ENDPOINTS.enterpriseAdmin.enterpriseDetails,
        { ...(enterpriseId ? { enterprise_id: enterpriseId } : {}) }
      );
      return response.data.data;
    } catch (error: any) {
      // Return null for 400 errors (e.g., no enterprise associated)
      if (error?.response?.status === 400) {
        return null;
      }
      throwApiError(error, 'Failed to fetch enterprise details');
    }
  }
  async getEnterpriseSummary(quarterId?: string, signal?: AbortSignal, enterpriseId?: string) {
    try {
      const response = await apiUtils.get<{ data: ProgramSummary }>(
        API_ENDPOINTS.enterpriseAdmin.enterpriseSummaryQuarterWise,
        { quarterId, ...(enterpriseId ? { enterprise_id: enterpriseId } : {}) },
        signal
      );
      return response.data.data;
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error; // Re-throw abort errors to be handled by caller
      }
      throwApiError(error, 'Failed to fetch enterprise summary');
      if (error?.response?.status === 400) {
        throw error;
      }
    }
  }

  async getMarketsSummary({
    params,
    quarterId,
    signal,
    enterpriseId,
  }: {
    params?: {
      sortBy?: string;
      page?: number;
      search?: string;
      limit?: number;
    };
    quarterId?: string;
    signal?: AbortSignal;
    enterpriseId?: string;
  } = {}) {
    try {
      const response = await apiUtils.get<MarketSummaryResponse>(
        API_ENDPOINTS.enterpriseAdmin.marketsSummaryQuarterWise,
        { ...params, quarterId, ...(enterpriseId ? { enterprise_id: enterpriseId } : {}) },
        signal
      );
      return response.data;
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('Request was aborted');
        throw error; // Re-throw abort errors to be handled by caller
      }
      if (error?.response?.status === 400) {
        return null;
      }
      throwApiError(error, 'Failed to fetch markets summary');
      return null;
    }
  }
  async downloadSummaryReport(): Promise<any> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.enterpriseAdmin.summaryReportDownload,
        {
          responseType: 'arraybuffer',
        }
      );
      console.log('response ::', response);
      console.log(response.data.byteLength);
      const bytes = new Uint8Array(response.data.slice(0, 20));
      console.log(bytes);
      return response;
    } catch (error) {
      throwApiError(error, 'Failed to download summary report');
    }
  }

  async getValueMatrixData(data: {
    country_code: string;
    state: string;
    city: string;
  }, enterpriseId?: string) {
    try {
      const response = await apiUtils.get<ValueMatrixResponse>(
        API_ENDPOINTS.enterpriseAdmin.valueMatrixData,
        { ...data, ...(enterpriseId ? { enterprise_id: enterpriseId } : {}) }
      );
      return response.data.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch value matrix data');
      return null;
    }
  }
}
export const enterpriseAdminService = new EnterpriseAdminService();
